import React from 'react';
import { type Line } from '../services/BackendDataService';
import { useTranslation } from 'react-i18next';
import { LineIcon } from './LineIcon';
import { useThemeClasses } from '../providers/ThemeProvider';
import { cx } from '@digitalisation/enlixy-comp';

interface LineCardProps {
  line: Line;
  onClick: (line: Line) => void;
  className?: string;
}

const LineCard: React.FC<LineCardProps> = ({ line, onClick, className = '' }) => {
  const { t } = useTranslation();
  const themeClasses = useThemeClasses();

  return (
    <div
      className={cx(
        themeClasses.cardBg,
        themeClasses.cardBorder,
        themeClasses.cardShadow,
        "relative p-4 rounded-lg border cursor-pointer transition-all duration-200 hover:shadow-lg transform hover:-translate-y-1 hover:rotate-1 overflow-hidden",
        className
      )}
      onClick={() => onClick(line)}
      style={{ perspective: '800px' }}
    >
      {/* Line Icon - Top Right */}
      <div className="absolute top-2 right-2 pointer-events-none">
        <LineIcon 
          machineCount={line.totalMachineCount ?? line.maschinen?.length ?? 0}
          size={28}
          className={themeClasses.textPrimary}
        />
      </div>
      
      {/* Line Info - Left side with padding for icon */}
      <div className="relative z-10 pr-16">
        <div className="flex items-start space-x-2 mb-3">
          <div className={cx(themeClasses.bgSuccess, "w-3 h-3 rounded-full flex-shrink-0 mt-1")} />
          <h5 className={cx(themeClasses.textSecondary, "text-lg font-semibold leading-tight")}>
            {line.linieName || t('unknownLine') || 'Unknown Line'}
          </h5>
        </div>
      </div>
      
      {/* Line Details */}
      <div className="space-y-2 text-sm">
        <div className="flex items-center space-x-2">
          <span className={cx(themeClasses.textSecondary, "font-medium")}>{t('lineNumber') || 'Line'}:</span> 
          <span className={cx(themeClasses.textSecondary)}>
            {line.linieNummer && line.linieNummer !== 'undefined' ? line.linieNummer : line.linieBK}
            {line.oemNummer && (
              <span className={themeClasses.textMuted}> ({line.oemNummer})</span>
            )}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <span className={cx(themeClasses.textSecondary, "font-medium")}>{t('factory') || 'Factory'}:</span> 
          <span className={cx(themeClasses.textSecondary)}>{line.factoryName || 'N/A'}</span>
        </div>
        <div className="flex items-center space-x-2">
          <span className={cx(themeClasses.textSecondary, "font-medium")}>{t('machines') || 'Machines'}:</span> 
          <span className={cx(themeClasses.textSecondary)}>{line.totalMachineCount ?? line.maschinen?.length ?? 0}</span>
        </div>
      </div>
    </div>
  );
};

export default LineCard;
